import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { FilterChips } from '~/components/FilterChips';
import { Table } from '~/components/Table';
import { showToast } from '~/components/Toast';
import { usePagination } from '~/hooks/usePagination';
import { useUser } from '~/modules/auth/hooks/useUser';
import { useBrand } from '~/modules/branding';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { FilterDrawer } from '~/modules/settings/components/FilterDrawer';
import { LABEL_TYPES } from '~/modules/settings/constants/Filter';
import { useFilters } from '~/modules/settings/hooks/useFilters';
import { useSearch } from '~/modules/settings/hooks/useSearch';
import type { LabelListItem } from '~settings/api/lib/schemas';
import { useLabelDelete } from '~settings/api/mutations/useLabelDelete';
import { useLabelList } from '~settings/api/queries/labelList';
import { ListControls } from '~settings/components/ListControls';
import { useModalContext } from '~settings/components/Modal';
import { SETTINGS_ROUTES } from '~settings/config/routes';
import { useDefaultSort } from '~settings/hooks/useDefaultSort';
import { LabelFormFilter } from '~settings/modules/label/components/LabelFormFilter';
import { getDeleteMessageFromStatus } from '~settings/modules/label/helpers';
import { translateOptions } from '~settings/utils/helpers';

import { useLabelsColumns } from './hooks/useLabelsColumns';

const emptyLabels: LabelListItem[] = [];

export function ListLabelPage() {
  const brand = useBrand();
  const { t } = useTranslation('settings');
  const { hasPermission } = useUser();
  const { confirm } = useModalContext();

  const [manageLabelEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsManageLabel,
  ]);

  const canCreateLabel =
    manageLabelEnabled &&
    hasPermission('LABEL.CREATE', { id: brand.name, type: 'APPLICATION' });

  const { page, setPageNumber, perPage, skip, take } = usePagination();
  const { filterData, drawerOpen, closeDrawer, openDrawer } = useFilters();
  const { sorting, setSorting, orderBy } = useDefaultSort({
    defaultSorting: [
      { id: 'type', desc: true },
      { id: 'name', desc: false },
    ],
    syncWithQueryParams: true,
  });

  const { searchTerm } = useSearch();

  const { isLoading, isFetching, isError, labelList } = useLabelList({
    skip,
    take,
    ...(orderBy && { orderBy }),
    ...filterData,
    ...(searchTerm && { search: searchTerm }),
  });

  const { mutate: deleteLabel } = useLabelDelete({
    onError: (error) => {
      const status = error?.response?.status;
      const message = getDeleteMessageFromStatus(status);

      showToast({
        title: t(message.title),
        description: t(message.description),
        type: 'error',
      });
    },
    onSuccess: (_, variables) => {
      showToast({
        title: t('label.deleteSuccessMessage', {
          name: variables.name,
          interpolation: { escapeValue: false },
        }),
        type: 'success',
      });
    },
  });

  const onDelete = (id: string, name: string) => {
    confirm({
      title: (
        <span>
          <Icon
            variant="regular"
            type="triangle-exclamation"
            className="mr-sm"
          />
          {t('label.deleteDialogTitle')}
        </span>
      ),
      message: t('label.deleteDialogMessage', {
        name,
        interpolation: { escapeValue: false },
      }),
      confirmText: t('buttons.confirmDelete'),
      cancelText: t('buttons.cancel'),
      onConfirm: () => {
        deleteLabel({ id, name });
      },
    });
  };

  const columns = useLabelsColumns(onDelete);

  const navigate = useNavigate();
  const onClick = (row: LabelListItem) => {
    navigate(SETTINGS_ROUTES.LABEL_DETAIL(row.id));
  };

  if (isLoading) {
    return <section className="py-md">{t('loading')}</section>;
  }

  if (isError) {
    // eslint-disable-next-line i18next/no-literal-string
    return <section className="py-md">Something went wrong.</section>;
  }

  return (
    <>
      <ListControls
        placeholder={t('searchLabels')}
        page={page}
        total={labelList?.total ?? 0}
        perPage={perPage}
        entity="labels"
        onFilterClick={openDrawer}
        internalFilter={
          <FilterChips
            options={translateOptions(LABEL_TYPES, t)}
            path="labelType"
          />
        }
        setPageNumber={setPageNumber}
        toolbarRightAddon={
          canCreateLabel ? (
            <Button
              asChild
              variant="primary"
              className="shrink-0 self-center"
              size="large"
            >
              <Link to={SETTINGS_ROUTES.LABEL_CREATE}>
                {t('label.addLink')}
              </Link>
            </Button>
          ) : null
        }
      />
      <Table
        data={labelList?.data ?? emptyLabels}
        columns={columns}
        onRowClick={onClick}
        sort={{
          sorting,
          onSortingChange: setSorting,
        }}
        skeletonLoading={{
          isLoading: isFetching,
          prefillRowsNumber: perPage,
        }}
        rowClassName="h-3xl"
        cellClassName="p-sm"
        data-testid="labels-table"
      />
      <FilterDrawer open={drawerOpen} onOpenChange={closeDrawer}>
        <LabelFormFilter open={drawerOpen} onApply={closeDrawer} />
      </FilterDrawer>
    </>
  );
}
ListLabelPage.displayName = 'ListLabelPage';
